import { Empleado } from "./../../shared/model/empleado/empleado.model";
import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Inject,
  AfterViewInit,
  Optional,
  ChangeDetectorRef,
} from "@angular/core";
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { Etapa } from "src/app/shared/model/gestion-proyectos/etapa/etapa";
import { Proyecto } from "src/app/shared/model/gestion-proyectos/proyecto";
import swal from "sweetalert2";
import { ProyectosService } from "../proyectos.service";
import { EmpleadoService } from "src/app/empleado/empleado.service";
import { Persona } from "src/app/shared/model/security/persona.model";
import { Puesto } from "src/app/shared/model/empleado/puesto.model";
import { Usuario } from "src/app/shared/model/security/usuario.model";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { Constantes } from "src/app/utilerias/constantes/constantes";
import { UtileriasService } from "src/app/utilerias/service/utilerias.service";
import { AuthService } from "src/app/security/auth.service";
import { CommonModule } from "@angular/common";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatSelectModule } from "@angular/material/select";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";

import { mostrarRechazoDelGuardado } from "../proyecto-pagina/rechazo-del-guardado";
import { indiceDeEtapaEditada, rechazoDelGuardado } from "../proyecto-pagina/etapas-versiones.util";
import { rutaDeRegreso } from "../proyecto-pagina/proyecto-pagina.rutas";
import { AsignacionEquipoQaComponent } from "./asignacion-equipo-qa/asignacion-equipo-qa.component";
import { AsignacionEquipoDesarrolloComponent } from "./asignacion-equipo-desarrollo/asignacion-equipo-desarrollo.component";

/** Áreas de puesto que pintan las listas de la etapa: 1 desarrollo, 2 QA. */
export const AREA_DESARROLLO = 1;
export const AREA_QA = 2;

/**
 * Recursos de la etapa al guardar.
 *
 * Cada lista (desarrollo y QA) trabaja sobre una COPIA de recursosAsignados
 * filtrada por el área del puesto, así que lo que manda es lo que quedó en
 * cada lista. Antes se unía la lista original con las dos nuevas y quien se
 * quitaba volvía a aparecer al guardar.
 * - `desarrollo` / `qa` en null: esa lista no reportó nada (no llegó a
 *   iniciar); se queda lo que ya tenía la etapa en esa área.
 * - Quien no tiene puesto o es de otra área no sale en ninguna lista: se
 *   conserva tal cual.
 */
export function construyeRecursosEtapa(
  originales: Empleado[] | null | undefined,
  desarrollo: Empleado[] | null,
  qa: Empleado[] | null,
): Empleado[] {
  const base = (Array.isArray(originales) ? originales : []).filter(Boolean);
  const deArea = (area: number) => base.filter((r) => r.puesto?.id_area === area);
  const fueraDeLasListas = base.filter(
    (r) => r.puesto?.id_area !== AREA_DESARROLLO && r.puesto?.id_area !== AREA_QA,
  );
  return unicosPorEmpleado([
    ...fueraDeLasListas,
    ...(desarrollo ?? deArea(AREA_DESARROLLO)),
    ...(qa ?? deArea(AREA_QA)),
  ]);
}

/**
 * Sin repetidos por idEmpleado (gana el último, en el lugar del primero). Un
 * renglón viejo sin idEmpleado no se junta con otros: se conserva.
 */
function unicosPorEmpleado(items: Empleado[]): Empleado[] {
  const unicos = new Map<string, Empleado>();
  items.filter(Boolean).forEach((item, indice) => {
    const clave = item.idEmpleado != null ? `e${item.idEmpleado}` : `sin-id-${indice}`;
    unicos.set(clave, item);
  });
  return Array.from(unicos.values());
}

@Component({
  selector: "app-modal-agregar-etapa",
  standalone: true,
  templateUrl: "./modal-agregar-etapa.component.html",
  styleUrls: ["./modal-agregar-etapa.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    MatExpansionModule,
    MatDatepickerModule,
    MatNativeDateModule,
    AsignacionEquipoQaComponent,
    AsignacionEquipoDesarrolloComponent,
  ],
})
export class ModalAgregarEtapaComponent implements OnInit, AfterViewInit {
  @ViewChild("nameStageCtrl") inputStageCtrl: ElementRef;

  step = 0;
  formStageProject: FormGroup;
  objectEtapa: Etapa;
  isValid = false;
  editorReady = false;
  listLider: Empleado[];
  listLiderQA: Empleado[];
  requestEmpleados = new ConsultaList<Empleado>();

  /** Lo que reporta cada lista; null mientras no reporte (ver construyeRecursosEtapa). */
  recursosDesarollo: Empleado[] | null = null;
  recursosQA: Empleado[] | null = null;

  minDate: Date;
  titleModal = "Crear etapa";
  pageTitle = "Crear etapa";
  pageDescription = "Configura responsables, fechas y el equipo operativo de la etapa.";
  projectContextLabel = "";
  modeEditable = false;
  nombreLider: string;
  currentFlowPrefix = "ODP";
  /** A dónde regresar al terminar (state.returnUrl, p. ej. la página del proyecto); null = la lista. */
  private returnUrl: string | null = null;

  constructor(
    @Optional() public dialogRef: MatDialogRef<ModalAgregarEtapaComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private proyectosService: ProyectosService,
    private empleadosService: EmpleadoService,
    private utilerias: UtileriasService,
    private authS: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.formStageProject = this.formBuilder.group({
      nameStageCtrl: ["", Validators.required],
      descriptionProjectCtrl: [""],
      bossCtrl: ["", Validators.required],
      bossQACtrl: ["", Validators.required],
      dateStartCtrl: ["", Validators.required],
      dateEndCtrl: ["", Validators.required],
    });

    this.requestEmpleados = new ConsultaList<Empleado>();
    this.requestEmpleados.regXPagina = 1000;
    this.requestEmpleados.regInicial = 0;
    this.requestEmpleados.bean = new Empleado();
    this.requestEmpleados.bean.usuario = new Usuario();
    this.requestEmpleados.bean.persona = new Persona();
    this.requestEmpleados.bean.puesto = new Puesto();

    this.consultarLideresDesarrollo();
    this.consultarLideresQA();

    if (!this.initializeEditorContext()) {
      return;
    }

    this.editorReady = true;
    this.cdr.detectChanges();
  }

  ngAfterViewInit(): void {
    this.isValid = false;
  }

  private initializeEditorContext(): boolean {
    this.currentFlowPrefix = this.resolveFlowPrefix();
    this.returnUrl = rutaDeRegreso(history.state?.returnUrl);

    const routeProjectId = this.route.snapshot.paramMap.get("idProyecto");
    const routeStageId = this.route.snapshot.paramMap.get("idEtapa");

    const storedProject =
      (history.state?.proyecto as Proyecto | undefined) ??
      this.data?.proyecto ??
      this.readStoredProjectState(routeProjectId);

    if (!storedProject) {
      this.showProjectUnavailableMessage(
        "Seleccione nuevamente el proyecto desde la visualizacion para continuar.",
      );
      return false;
    }

    const hydratedProject = this.hydrateProject(storedProject);
    const storedStage =
      (history.state?.etapa as Etapa | undefined) ??
      this.data?.etapa ??
      this.findStageInProject(hydratedProject, routeStageId);

    this.data = {
      proyecto: hydratedProject,
      etapa: storedStage ?? null,
    };

    this.minDate = hydratedProject.fechaInicio
      ? new Date(hydratedProject.fechaInicio)
      : new Date();
    this.projectContextLabel =
      hydratedProject.nombre ||
      hydratedProject.nombre_corto ||
      hydratedProject.idProyecto ||
      "Proyecto seleccionado";

    if (storedStage) {
      this.objectEtapa = this.hydrateStage(storedStage);
      this.formStageProject.disable({ emitEvent: false });
      this.formStageProject.controls.bossCtrl.enable({ emitEvent: false });
      this.formStageProject.controls.bossQACtrl.enable({ emitEvent: false });
      this.modeEditable = true;
      this.pageTitle = "Editar etapa";
      this.pageDescription =
        "Ajusta responsables y recursos sin salir del flujo de proyectos.";
      this.titleModal = `Editar etapa${this.objectEtapa.nombre ? " - " + this.objectEtapa.nombre : ""}`;
      this.sonRecursosValidos();
      return true;
    }

    this.objectEtapa = new Etapa();
    this.pageTitle = "Crear etapa";
    this.pageDescription =
      "Registra una nueva etapa y asigna su equipo principal desde una sola pantalla.";
    this.titleModal = "Crear etapa";
    this.modeEditable = false;
    this.step = 0;
    return true;
  }

  private resolveFlowPrefix(): string {
    const firstSegment = this.router.url.split("/")[1] || "ODP";
    if (["ODP", "LIDER", "AUDITORIA"].includes(firstSegment)) {
      return firstSegment;
    }
    if (firstSegment === "VISUALIZARPROY") {
      return "VISUALIZARPROY";
    }
    return "ODP";
  }

  private readStoredProjectState(projectId: string | null): Proyecto | undefined {
    if (!projectId) {
      return undefined;
    }

    const rawState = sessionStorage.getItem(this.getProjectStorageKey(projectId));
    if (!rawState) {
      return undefined;
    }

    try {
      return JSON.parse(rawState) as Proyecto;
    } catch {
      sessionStorage.removeItem(this.getProjectStorageKey(projectId));
      return undefined;
    }
  }

  private getProjectStorageKey(projectId: number | string): string {
    return `adm-proyectos:${this.currentFlowPrefix}:project:${projectId}`;
  }

  private getListRoute(): string {
    return this.currentFlowPrefix === "VISUALIZARPROY"
      ? "/VISUALIZARPROY"
      : `/${this.currentFlowPrefix}/VISUALIZARPROY`;
  }

  private navigateBackToList(): void {
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
      return;
    }
    this.router.navigate([this.getListRoute()]);
  }

  private showProjectUnavailableMessage(message: string): void {
    queueMicrotask(() => {
      swal
        .fire({
          title: "Informacion no disponible",
          text: message,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-success",
          },
          confirmButtonText: "Aceptar",
          icon: "info",
        })
        .then(() => this.navigateBackToList());
    });
  }

  private hydrateProject(source: Proyecto): Proyecto {
    const hydrated = Object.assign(
      new Proyecto(),
      JSON.parse(JSON.stringify(source ?? {})),
    ) as Proyecto;

    hydrated.etapas = Array.isArray(hydrated.etapas) ? hydrated.etapas : [];
    return hydrated;
  }

  private hydrateStage(source: Etapa): Etapa {
    const hydrated = Object.assign(
      new Etapa(),
      JSON.parse(JSON.stringify(source ?? {})),
    ) as Etapa;

    hydrated.recursosAsignados = Array.isArray(hydrated.recursosAsignados)
      ? hydrated.recursosAsignados
      : [];
    return hydrated;
  }

  private findStageInProject(
    proyecto: Proyecto | undefined,
    routeStageId: string | null,
  ): Etapa | undefined {
    if (!proyecto?.etapas?.length || !routeStageId) {
      return undefined;
    }

    const decodedStageId = decodeURIComponent(routeStageId);

    return proyecto.etapas.find((etapa) => {
      return (
        String(etapa.idEtapa ?? "") === decodedStageId ||
        String(etapa.id ?? "") === decodedStageId ||
        String(etapa.nombre ?? "") === decodedStageId
      );
    });
  }

  consultarLideresDesarrollo(): void {
    this.requestEmpleados.bean.puesto.id = 1;
    this.requestEmpleados.bean.puesto.id_area = 1;
    this.empleadosService
      .consultaEmpleadosPorPuesto(this.requestEmpleados)
      .subscribe((response) => {
        if (response.list.length !== 0) {
          this.listLider = response.list;
        }
      });
  }

  consultarLideresQA(): void {
    this.requestEmpleados.bean.puesto.id = 3;
    this.requestEmpleados.bean.puesto.id_area = 2;
    this.empleadosService
      .consultaEmpleadosPorPuesto(this.requestEmpleados)
      .subscribe((response) => {
        if (response.list.length !== 0) {
          this.listLiderQA = response.list;
        }
      });
  }

  clear(): void {
    if (this.modeEditable) {
      this.formStageProject.controls.bossCtrl.reset();
      this.formStageProject.controls.bossQACtrl.reset();
      this.objectEtapa.idLiderEtapa = null;
      this.objectEtapa.idLiderEtapaQA = null;
      return;
    }

    this.formStageProject.reset();
    this.objectEtapa = new Etapa();
    this.recursosDesarollo = [];
    this.recursosQA = [];
    this.isValid = false;
    this.step = 0;
    this.inputStageCtrl?.nativeElement?.focus();
  }

  onNoClick(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      return;
    }

    this.navigateBackToList();
  }

  canSaveStage(): boolean {
    return this.modeEditable || this.formStageProject.valid;
  }

  save(): void {
    if (!this.data?.proyecto) {
      return;
    }

    if (!this.isValidDates()) {
      swal.fire({
        title: "ERROR",
        text: "Por favor, ingrese un período de fechas válido...",
        buttonsStyling: false,
        customClass: {
          confirmButton: "btn btn-success",
          cancelButton: "btn btn-danger",
        },
        confirmButtonText: "Aceptar",
        icon: "warning",
      });
      return;
    }

    if (!this.data.proyecto.etapas) {
      this.data.proyecto.etapas = [];
    }

    this.objectEtapa.recursosAsignados = construyeRecursosEtapa(
      this.objectEtapa.recursosAsignados,
      this.recursosDesarollo,
      this.recursosQA,
    );

    const lider = this.listLider?.find(
      (item) => item.idEmpleado == this.objectEtapa.idLiderEtapa,
    );
    if (lider?.persona?.nombreCompleto) {
      this.objectEtapa.nombreLider = lider.persona.nombreCompleto;
    }

    const liderQA = this.listLiderQA?.find(
      (item) => item.idEmpleado == this.objectEtapa.idLiderEtapaQA,
    );
    if (liderQA?.persona?.nombreCompleto) {
      this.objectEtapa.nombreLiderQA = liderQA.persona.nombreCompleto;
    }

    // Si el servidor rechaza el guardado, el proyecto en memoria vuelve a como
    // estaba: otro intento no debe agregar la etapa dos veces.
    const etapasAntes = [...this.data.proyecto.etapas];
    if (!this.modeEditable) {
      this.data.proyecto.etapas.push(this.objectEtapa);
    } else {
      const etapaEditar = this.findEditableStageIndex();
      if (etapaEditar !== -1) {
        this.data.proyecto.etapas[etapaEditar] = this.objectEtapa;
      }
    }

    this.proyectosService.saveProyect(this.data.proyecto).subscribe((response) => {
      // Rechazos del guardado (etapas y versiones, §5.8): no se guardó nada.
      const rechazo = rechazoDelGuardado(response);
      if (rechazo) {
        this.data.proyecto.etapas = etapasAntes;
        mostrarRechazoDelGuardado(rechazo);
        return;
      }
      swal
        .fire({
          title: "ÉXITO",
          text: response.pMensaje,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-success",
            cancelButton: "btn btn-danger",
          },
          confirmButtonText: "Aceptar",
          icon: "success",
        })
        .then(() => {
          if (this.dialogRef) {
            this.dialogRef.close(true);
          } else {
            this.navigateBackToList();
          }

          if (Constantes.DIEZ === this.utilerias.convertirANumero(response.pCodRetorno)) {
            this.authS.logout();
          }
        });
    });
  }

  /** Por id, luego por idEtapa y solo al final por nombre (indiceDeEtapaEditada). */
  private findEditableStageIndex(): number {
    return indiceDeEtapaEditada(this.data.proyecto.etapas, this.objectEtapa);
  }

  setStep(index: number) {
    this.step = index;
  }

  nextStep() {
    this.step++;
  }

  prevStep() {
    this.step--;
  }

  selectedBoss(): void {
    this.sonRecursosValidos();
  }

  selectedBossQA(): void {
    this.sonRecursosValidos();
  }

  formatFecha(str: any): string {
    const date = new Date(str);
    const mnth = ("0" + (date.getMonth() + 1)).slice(-2);
    const day = ("0" + date.getDate()).slice(-2);
    return [date.getFullYear(), mnth, day].join("-");
  }

  isValidDates(): boolean {
    const startDate = this.formStageProject.controls.dateStartCtrl.value;
    const endDate = this.formStageProject.controls.dateEndCtrl.value;
    return this.isValidRangeofDates(startDate, endDate);
  }

  isValidRangeofDates(start: string, end: string): boolean {
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (startDate.getTime() === endDate.getTime()) {
      return false;
    }
    if (startDate.getTime() > endDate.getTime()) {
      return false;
    }
    if (endDate.getTime() < startDate.getTime()) {
      return false;
    }
    return true;
  }

  resourcesSelectedDevelopers(items: Array<Empleado>): void {
    this.recursosDesarollo = items;
    this.sonRecursosValidos();
  }

  resourcesSelectedQA(items: Array<Empleado>): void {
    this.recursosQA = items;
    this.sonRecursosValidos();
  }

  sonRecursosValidos(): void {
    this.isValid =
      (!!this.recursosQA && this.recursosQA.length > 0) &&
      (!!this.recursosDesarollo && this.recursosDesarollo.length > 0);
  }
}
